import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import "../styles/certificates.css";

import certBg         from "../photos/certificates.jpg";
import arduinoWs      from "../photos/certificates_arduinows.jpg";
import backend        from "../photos/certificates_backend.jpg";
import cidr           from "../photos/certificates_cidr.jpg";
import cpelympics     from "../photos/certificates_cpelympics.jpg";
import dl2Y1S         from "../photos/certificates_dl2Y1S.jpg";
import dl3Y1s         from "../photos/certificates_dl3Y1s.jpg";
import maesep         from "../photos/certificates_maesep.jpg";
import pl1Y1S         from "../photos/certificates_pl1Y1S.jpg";
import pl1Y2S         from "../photos/certificates_pl1Y2S.jpg";
import pl2Y2S         from "../photos/certificates_pl2Y2S.jpg";
import qexam          from "../photos/certificates_qexam.jpg";

/* ─── Data ─── */
const CATEGORIES = ["All", "Academic", "Workshop", "Event"];

const certificates = [
  {
    id: "pl1y1s",
    img: pl1Y1S,
    title: "President's Lister",
    subtitle: "Academic Excellence Certificate",
    date: "February 7, 2024",
    detail: "First Year, First Semester",
    organizer: "FAITH Colleges",
    category: "Academic",
    accent: "#44f9ff",
  },
  {
    id: "pl1y2s",
    img: pl1Y2S,
    title: "President's Lister",
    subtitle: "Academic Excellence Certificate",
    date: "August 28, 2024",
    detail: "First Year, Second Semester",
    organizer: "FAITH Colleges",
    category: "Academic",
    accent: "#44f9ff",
  },
  {
    id: "qexam",
    img: qexam,
    title: "Qualifying Examination",
    subtitle: "Top 4",
    date: "August 15, 2024",
    detail: "",
    organizer: "FAITH Colleges – College of Engineering",
    category: "Academic",
    accent: "#f9d744",
  },
  {
    id: "dl2y1s",
    img: dl2Y1S,
    title: "Dean's Lister",
    subtitle: "Academic Excellence Certificate",
    date: "February 19, 2025",
    detail: "Second Year, First Semester",
    organizer: "FAITH Colleges",
    category: "Academic",
    accent: "#44f9ff",
  },
  {
    id: "pl2y2s",
    img: pl2Y2S,
    title: "President's Lister",
    subtitle: "Academic Excellence Certificate",
    date: "August 6, 2025",
    detail: "Second Year, Second Semester",
    organizer: "FAITH Colleges",
    category: "Academic",
    accent: "#44f9ff",
  },
  {
    id: "cidr",
    img: cidr,
    title: "CIDR Conference",
    subtitle: "Connect. Innovate. Design. Revolutionize.",
    date: "August 12, 2025",
    detail: "3rd Annual Conference — Center for Integrated Circuit and Device Research · Strengthening the Philippines' Microelectronics Ecosystem",
    organizer: "Louis P. Alarcon, Ph.D.",
    category: "Event",
    accent: "#ff7844",
  },
  {
    id: "arduinows",
    img: arduinoWs,
    title: "Arduino Workshop Part 2",
    subtitle: "Integration of Hardware",
    date: "September 13, 2025",
    detail: "",
    organizer: "ICpEP.SE-FC · Engr. Marco A. Burdeos",
    tutor: "Engr. Marco A. Burdeos",
    category: "Workshop",
    accent: "#44ff8f",
  },
  {
    id: "dl3y1s",
    img: dl3Y1s,
    title: "Dean's Lister",
    subtitle: "Academic Excellence Certificate",
    date: "January 14, 2026",
    detail: "Third Year, First Semester",
    organizer: "FAITH Colleges",
    category: "Academic",
    accent: "#44f9ff",
  },
  {
    id: "backend",
    img: backend,
    title: "Backend Development Foundations",
    subtitle: "From Concepts to Practice",
    date: "March 24, 2026",
    detail: "",
    organizer: "ICpEP.SE-FC · Engr. Marco A. Burdeos",
    tutor: "Engr. Ivan Eser",
    category: "Workshop",
    accent: "#44ff8f",
  },
  {
    id: "cpelympics",
    img: cpelympics,
    title: "CpElympics 2026",
    subtitle: "Game Changers: Driven by Passion, United by Sport",
    date: "March 28, 2026",
    detail: "",
    organizer: "ICpEP.SE",
    category: "Event",
    accent: "#ff7844",
  },
  {
    id: "maesep",
    img: maesep,
    title: "MaESEP",
    subtitle: "Mathematics and Engineering Science Enhancement Program · Accelerated",
    date: "April 11, 2026",
    detail: "",
    organizer: "Engr. Sherald G. Agustin",
    tutor: "Engr. Daren Valenzuela",
    category: "Workshop",
    accent: "#44ff8f",
  },
];

/* ─── Intersection reveal hook ─── */
function useReveal(selector = ".cert-reveal", deps = []) {
  useEffect(() => {
    const nodes = document.querySelectorAll(selector);
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            observer.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selector, ...deps]);
}

/* ─── Modal ─── */
function CertModal({ cert, onClose, onPrev, onNext, hasPrev, hasNext }) {
  // Close on backdrop click
  const handleBackdrop = useCallback(
    (e) => { if (e.target === e.currentTarget) onClose(); },
    [onClose]
  );

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) onPrev();
      if (e.key === "ArrowRight" && hasNext) onNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  if (!cert) return null;

  const modal = (
    <div className="cert-modal-backdrop" onClick={handleBackdrop}>
      <div className="cert-modal" style={{ "--modal-accent": cert.accent }}>

        {/* Close */}
        <button className="cert-modal__close" onClick={onClose} aria-label="Close">
          <span>✕</span>
        </button>

        {/* Navigation */}
        {hasPrev && (
          <button className="cert-modal__nav cert-modal__nav--prev" onClick={onPrev} aria-label="Previous">
            ‹
          </button>
        )}
        {hasNext && (
          <button className="cert-modal__nav cert-modal__nav--next" onClick={onNext} aria-label="Next">
            ›
          </button>
        )}

        {/* Image */}
        <div className="cert-modal__img-wrap">
          <img src={cert.img} alt={cert.title} className="cert-modal__img" />
          <div className="cert-modal__img-overlay" />
          {/* Category badge on image */}
          <span
            className="cert-modal__cat-badge"
            style={{ background: cert.accent + "22", color: cert.accent, borderColor: cert.accent + "44" }}
          >
            {cert.category}
          </span>
        </div>

        {/* Info */}
        <div className="cert-modal__info">
          <p className="cert-modal__subtitle">{cert.subtitle}</p>
          <h2 className="cert-modal__title">{cert.title}</h2>

          <div className="cert-modal__divider" style={{ background: cert.accent + "55" }} />

          <div className="cert-modal__meta">
            <div className="cert-modal__meta-row">
              <span className="cert-modal__meta-label">Date</span>
              <span className="cert-modal__meta-value">{cert.date}</span>
            </div>
            {cert.detail && (
              <div className="cert-modal__meta-row">
                <span className="cert-modal__meta-label">Detail</span>
                <span className="cert-modal__meta-value">{cert.detail}</span>
              </div>
            )}
            <div className="cert-modal__meta-row">
              <span className="cert-modal__meta-label">Organizer</span>
              <span className="cert-modal__meta-value">{cert.organizer}</span>
            </div>
            {cert.tutor && (
              <div className="cert-modal__meta-row">
                <span className="cert-modal__meta-label">Tutor</span>
                <span className="cert-modal__meta-value">{cert.tutor}</span>
              </div>
            )}
          </div>

          {/* Decorative accent line */}
          <div
            className="cert-modal__accent-line"
            style={{ background: `linear-gradient(90deg, ${cert.accent}88, transparent)` }}
          />
        </div>

      </div>
    </div>
  );

  // Portal to document.body so modal always overlays the full viewport
  // regardless of parent overflow, transform, or scroll position
  return createPortal(modal, document.body);
}

/* ─── Card ─── */
function CertCard({ cert, index, onClick }) {
  return (
    <div
      className="cert-card"
      style={{ "--card-accent": cert.accent }}
      onClick={onClick}
      tabIndex={0}
      role="button"
      aria-label={`View ${cert.title}`}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
    >
      {/* Thumbnail */}
      <div className="cert-card__thumb">
        <img src={cert.img} alt={cert.title} className="cert-card__img" loading="lazy" />
        <div className="cert-card__img-overlay" />
        <div className="cert-card__hover-cta">
          <span className="cert-card__hover-icon">⤢</span>
          <span className="cert-card__hover-text">View</span>
        </div>
      </div>

      {/* Body */}
      <div className="cert-card__body">
        <div className="cert-card__category" style={{ color: cert.accent }}>
          {cert.category}
        </div>
        <p className="cert-card__title">{cert.title}</p>
        <p className="cert-card__subtitle">{cert.subtitle}</p>
        <p className="cert-card__date">{cert.date}</p>
      </div>

      {/* Bottom accent bar */}
      <div className="cert-card__accent-bar" style={{ background: cert.accent }} />
    </div>
  );
}

/* ─── Stats bar ─── */
function StatsBar() {
  const counts = {
    Total: certificates.length,
    Academic: certificates.filter((c) => c.category === "Academic").length,
    Workshop: certificates.filter((c) => c.category === "Workshop").length,
    Event: certificates.filter((c) => c.category === "Event").length,
  };
  return (
    <div className="cert-stats cert-reveal">
      {Object.entries(counts).map(([label, value]) => (
        <div key={label} className="cert-stat">
          <span className="cert-stat__value">{value}</span>
          <span className="cert-stat__label">{label}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Main Export ─── */
export default function Certificates() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [modalIndex, setModalIndex] = useState(null); // index into filtered list

  // Only observe static elements (stats, filters) — not cards
  useReveal(".cert-reveal", []);

  const filtered = activeCategory === "All"
    ? certificates
    : certificates.filter((c) => c.category === activeCategory);

  const openModal = (idx) => setModalIndex(idx);
  const closeModal = () => setModalIndex(null);
  const prevModal = () => setModalIndex((i) => Math.max(0, i - 1));
  const nextModal = () => setModalIndex((i) => Math.min(filtered.length - 1, i + 1));

  return (
    <div className="cert-page">

      {/* ════ HERO ════ */}
      <div className="cert-hero">
        <img src={certBg} alt="Certificates background" className="cert-hero__bg" />
        <div className="cert-hero__overlay" />

        {/* Decorative grid lines */}
        <div className="cert-hero__grid" aria-hidden="true">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="cert-hero__grid-line" style={{ "--i": i }} />
          ))}
        </div>

        <div className="cert-hero__content">
          <h1 className="cert-hero__title">
            Credentials &<span className="cert-hero__title-accent"> Recognition</span>
          </h1>
          <p className="cert-hero__sub">
            A curated record of workshops, academic honors,<br />
            conferences, and events that shaped my journey.
          </p>
        </div>
      </div>

      {/* ════ STATS ════ */}
      <div className="cert-page__inner">
        <StatsBar />

        {/* ════ FILTER TABS ════ */}
        <div className="cert-filters cert-reveal">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`cert-filter-btn ${activeCategory === cat ? "is-active" : ""}`}
              onClick={() => { setActiveCategory(cat); setModalIndex(null); }}
            >
              {cat}
              <span className="cert-filter-btn__count">
                {cat === "All" ? certificates.length : certificates.filter((c) => c.category === cat).length}
              </span>
            </button>
          ))}
        </div>

        {/* ════ GRID ════ */}
        <div className="cert-grid" key={activeCategory}>
          {filtered.map((cert, idx) => (
            <CertCard
              key={cert.id}
              cert={cert}
              index={idx}
              onClick={() => openModal(idx)}
            />
          ))}
        </div>
      </div>

      {/* ════ MODAL ════ */}
      {modalIndex !== null && (
        <CertModal
          cert={filtered[modalIndex]}
          onClose={closeModal}
          onPrev={prevModal}
          onNext={nextModal}
          hasPrev={modalIndex > 0}
          hasNext={modalIndex < filtered.length - 1}
        />
      )}
    </div>
  );
}